import type { TrailSegment } from './trail-extractor.js'
import { logger } from '../../utils/logger.js'

const log = logger.create('trail-network')

const NODE_MERGE_THRESHOLD = 0.0003 // ~33m in degrees at mid-latitudes
const TOBLER_MAX_SPEED_MS = 6 / 3.6

interface TrailNode {
  id: number
  lng: number
  lat: number
}

interface TrailEdge {
  id: number
  nodeA: number
  nodeB: number
  coordinates: [number, number][]
  costAtoB: number
  costBtoA: number
  distanceMeters: number
  vertexDensity: number
  dead?: boolean
}

interface AdjEntry {
  edgeId: number
  targetNode: number
  cost: number
}

export interface TrailNetwork {
  nodes: TrailNode[]
  edges: TrailEdge[]
  adjacency: Map<number, AdjEntry[]>
}

export interface TrailRouteResult {
  path: [number, number][]
  distanceMeters: number
  timeSeconds: number
  elevationGainMeters: number
  elevationLossMeters: number
}

export interface ElevationSampler {
  sample(lng: number, lat: number): number
}

export function createElevationSampler(
  elevationGrid: Float32Array,
  gridWidth: number,
  gridHeight: number,
  originLng: number,
  originLat: number,
  lngPerCol: number,
  latPerRow: number
): ElevationSampler {
  return {
    sample(lng: number, lat: number): number {
      const col = Math.round((lng - originLng) / lngPerCol)
      const row = Math.round((lat - originLat) / latPerRow)
      if (col < 0 || col >= gridWidth || row < 0 || row >= gridHeight) return 0
      const v = elevationGrid[row * gridWidth + col]
      return isNaN(v) ? 0 : v
    },
  }
}

export function buildTrailNetwork(trails: TrailSegment[], sampler: ElevationSampler): TrailNetwork {
  const nodes: TrailNode[] = []
  const nodeGrid = new Map<string, number[]>()

  function findOrCreateNode(lng: number, lat: number): number {
    const hx = Math.floor(lng / NODE_MERGE_THRESHOLD)
    const hy = Math.floor(lat / NODE_MERGE_THRESHOLD)
    const threshSq = NODE_MERGE_THRESHOLD * NODE_MERGE_THRESHOLD

    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const key = `${hx + dx},${hy + dy}`
        const ids = nodeGrid.get(key)
        if (!ids) continue
        for (const id of ids) {
          const n = nodes[id]
          const dlng = n.lng - lng
          const dlat = n.lat - lat
          if (dlng * dlng + dlat * dlat < threshSq) {
            return id
          }
        }
      }
    }

    const id = nodes.length
    nodes.push({ id, lng, lat })
    const key = `${hx},${hy}`
    const existing = nodeGrid.get(key)
    if (existing) existing.push(id)
    else nodeGrid.set(key, [id])
    return id
  }

  const edges: TrailEdge[] = []

  for (const trail of trails) {
    const coords = trail.coordinates
    if (coords.length < 2) continue

    const nodeA = findOrCreateNode(coords[0][0], coords[0][1])
    const nodeB = findOrCreateNode(coords[coords.length - 1][0], coords[coords.length - 1][1])

    const { costAtoB, costBtoA, distanceMeters } = computeEdgeCosts(coords, sampler)
    const vertexDensity = distanceMeters > 0 ? coords.length / (distanceMeters / 1000) : 0

    edges.push({
      id: edges.length,
      nodeA,
      nodeB,
      coordinates: coords,
      costAtoB,
      costBtoA,
      distanceMeters,
      vertexDensity,
    })
  }

  const dedupEdges = deduplicateEdges(edges)

  const adjacency = new Map<number, AdjEntry[]>()
  for (const node of nodes) adjacency.set(node.id, [])

  for (const edge of dedupEdges) {
    if (edge.dead) continue
    adjacency.get(edge.nodeA)!.push({
      edgeId: edge.id,
      targetNode: edge.nodeB,
      cost: edge.costAtoB,
    })
    adjacency.get(edge.nodeB)!.push({
      edgeId: edge.id,
      targetNode: edge.nodeA,
      cost: edge.costBtoA,
    })
  }

  log.info(
    `Trail network: ${nodes.length} nodes, ${dedupEdges.length} edges ` +
      `(${edges.length - dedupEdges.length} duplicates removed)`
  )

  const components = countConnectedComponents(nodes, adjacency)
  const maxDegree = Math.max(...[...adjacency.values()].map(a => a.length))
  log.info(
    `Trail network connectivity: ${components} connected components, max degree ${maxDegree}`
  )

  return { nodes, edges: dedupEdges, adjacency }
}

function computeEdgeCosts(
  coords: [number, number][],
  sampler: ElevationSampler
): { costAtoB: number; costBtoA: number; distanceMeters: number } {
  let distM = 0
  let timeAtoB = 0
  let timeBtoA = 0

  let prevElev = sampler.sample(coords[0][0], coords[0][1])
  for (let i = 1; i < coords.length; i++) {
    const d = haversineMeters(coords[i - 1], coords[i])
    const elev = sampler.sample(coords[i][0], coords[i][1])
    distM += d
    if (d > 0.1) {
      const slope = (elev - prevElev) / d
      const speedAB = toblerSpeedMs(slope)
      const speedBA = toblerSpeedMs(-slope)
      if (speedAB > 0) timeAtoB += d / speedAB
      if (speedBA > 0) timeBtoA += d / speedBA
    }
    prevElev = elev
  }

  return { costAtoB: timeAtoB, costBtoA: timeBtoA, distanceMeters: distM }
}

function deduplicateEdges(edges: TrailEdge[]): TrailEdge[] {
  const groups = new Map<string, TrailEdge[]>()
  for (const edge of edges) {
    const key =
      edge.nodeA < edge.nodeB ? `${edge.nodeA}-${edge.nodeB}` : `${edge.nodeB}-${edge.nodeA}`
    const group = groups.get(key)
    if (group) group.push(edge)
    else groups.set(key, [edge])
  }

  const result: TrailEdge[] = []
  for (const group of groups.values()) {
    if (group.length === 1) {
      result.push(group[0])
      continue
    }
    group.sort((a, b) => b.vertexDensity - a.vertexDensity)
    result.push(group[0])
  }
  return result
}

// ─── Graph Routing ──────────────────────────────────────────────────

export function routeOnTrailNetwork(
  network: TrailNetwork,
  start: [number, number],
  end: [number, number],
  sampler: ElevationSampler
): TrailRouteResult | null {
  const nodes = [...network.nodes]
  const edges = [...network.edges]
  const adjacency = new Map<number, AdjEntry[]>()
  for (const [k, v] of network.adjacency) adjacency.set(k, [...v])

  const startMatch = findNearestEdge(edges, start)
  const endMatch = findNearestEdge(edges, end)
  if (!startMatch || !endMatch) return null

  const startNodeId = insertSplitNode(nodes, edges, adjacency, startMatch, sampler)
  const endNodeId = insertSplitNode(nodes, edges, adjacency, endMatch, sampler)

  const graphResult = astarGraph(nodes, edges, adjacency, startNodeId, endNodeId)
  if (!graphResult) return null

  const { elevGain, elevLoss } = computeElevationStats(graphResult.path, sampler)

  return {
    path: graphResult.path,
    distanceMeters: graphResult.distanceMeters,
    timeSeconds: graphResult.timeSeconds,
    elevationGainMeters: elevGain,
    elevationLossMeters: elevLoss,
  }
}

interface EdgeMatch {
  edgeId: number
  edgeIdx: number
  t: number
  point: [number, number]
  distSq: number
}

function findNearestEdge(edges: TrailEdge[], point: [number, number]): EdgeMatch | null {
  let best: EdgeMatch | null = null

  for (const edge of edges) {
    if (edge.dead) continue
    const coords = edge.coordinates
    for (let i = 0; i < coords.length - 1; i++) {
      const proj = projectOntoSegment(point, coords[i], coords[i + 1])
      if (!best || proj.distSq < best.distSq) {
        best = {
          edgeId: edge.id,
          edgeIdx: i,
          t: proj.t,
          point: proj.point,
          distSq: proj.distSq,
        }
      }
    }
  }

  return best
}

function projectOntoSegment(
  p: [number, number],
  a: [number, number],
  b: [number, number]
): { point: [number, number]; t: number; distSq: number } {
  const dx = b[0] - a[0]
  const dy = b[1] - a[1]
  const lenSq = dx * dx + dy * dy
  if (lenSq < 1e-20) {
    return {
      point: [a[0], a[1]],
      t: 0,
      distSq: (p[0] - a[0]) ** 2 + (p[1] - a[1]) ** 2,
    }
  }
  let t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / lenSq
  t = Math.max(0, Math.min(1, t))
  const px = a[0] + t * dx
  const py = a[1] + t * dy
  return { point: [px, py], t, distSq: (p[0] - px) ** 2 + (p[1] - py) ** 2 }
}

function insertSplitNode(
  nodes: TrailNode[],
  edges: TrailEdge[],
  adjacency: Map<number, AdjEntry[]>,
  match: EdgeMatch,
  sampler: ElevationSampler
): number {
  const edge = edges.find(e => e.id === match.edgeId)!
  const splitPt = match.point

  const newNodeId = nodes.length
  nodes.push({ id: newNodeId, lng: splitPt[0], lat: splitPt[1] })
  adjacency.set(newNodeId, [])

  const coordsBefore = edge.coordinates.slice(0, match.edgeIdx + 1)
  coordsBefore.push(splitPt)
  const coordsAfter: [number, number][] = [splitPt, ...edge.coordinates.slice(match.edgeIdx + 1)]

  const costsBefore = computeEdgeCosts(coordsBefore, sampler)
  const costsAfter = computeEdgeCosts(coordsAfter, sampler)

  const edgeA: TrailEdge = {
    id: edges.length,
    nodeA: edge.nodeA,
    nodeB: newNodeId,
    coordinates: coordsBefore,
    costAtoB: costsBefore.costAtoB,
    costBtoA: costsBefore.costBtoA,
    distanceMeters: costsBefore.distanceMeters,
    vertexDensity: edge.vertexDensity,
  }
  const edgeB: TrailEdge = {
    id: edges.length + 1,
    nodeA: newNodeId,
    nodeB: edge.nodeB,
    coordinates: coordsAfter,
    costAtoB: costsAfter.costAtoB,
    costBtoA: costsAfter.costBtoA,
    distanceMeters: costsAfter.distanceMeters,
    vertexDensity: edge.vertexDensity,
  }

  edges.push(edgeA, edgeB)
  edge.dead = true

  const adjA = adjacency.get(edge.nodeA)!
  const idxA = adjA.findIndex(e => e.edgeId === edge.id)
  if (idxA >= 0) adjA.splice(idxA, 1)

  const adjB = adjacency.get(edge.nodeB)!
  const idxB = adjB.findIndex(e => e.edgeId === edge.id)
  if (idxB >= 0) adjB.splice(idxB, 1)

  adjacency.get(edge.nodeA)!.push({
    edgeId: edgeA.id,
    targetNode: newNodeId,
    cost: edgeA.costAtoB,
  })
  adjacency.get(newNodeId)!.push({
    edgeId: edgeA.id,
    targetNode: edge.nodeA,
    cost: edgeA.costBtoA,
  })
  adjacency.get(newNodeId)!.push({
    edgeId: edgeB.id,
    targetNode: edge.nodeB,
    cost: edgeB.costAtoB,
  })
  adjacency.get(edge.nodeB)!.push({
    edgeId: edgeB.id,
    targetNode: newNodeId,
    cost: edgeB.costBtoA,
  })

  return newNodeId
}

interface GraphRouteResult {
  path: [number, number][]
  distanceMeters: number
  timeSeconds: number
}

function astarGraph(
  nodes: TrailNode[],
  edges: TrailEdge[],
  adjacency: Map<number, AdjEntry[]>,
  startId: number,
  endId: number
): GraphRouteResult | null {
  const endNode = nodes[endId]
  const gCost = new Map<number, number>()
  const parent = new Map<number, { nodeId: number; edgeId: number }>()
  const open = new Map<number, number>()
  const closed = new Set<number>()

  gCost.set(startId, 0)
  open.set(startId, graphHeuristic(nodes[startId], endNode))

  while (open.size > 0) {
    let bestId = -1
    let bestF = Infinity
    for (const [id, f] of open) {
      if (f < bestF) {
        bestF = f
        bestId = id
      }
    }

    if (bestId === endId) {
      return reconstructGraphPath(bestId, parent, edges, gCost)
    }

    open.delete(bestId)
    closed.add(bestId)

    const adj = adjacency.get(bestId)
    if (!adj) continue

    const g = gCost.get(bestId)!
    for (const { edgeId, targetNode, cost } of adj) {
      if (closed.has(targetNode)) continue
      const newG = g + cost
      const existingG = gCost.get(targetNode)
      if (existingG === undefined || newG < existingG) {
        gCost.set(targetNode, newG)
        parent.set(targetNode, { nodeId: bestId, edgeId })
        open.set(targetNode, newG + graphHeuristic(nodes[targetNode], endNode))
      }
    }
  }

  return null
}

function graphHeuristic(from: TrailNode, to: TrailNode): number {
  const dist = haversineMeters([from.lng, from.lat], [to.lng, to.lat])
  return dist / TOBLER_MAX_SPEED_MS
}

function reconstructGraphPath(
  endId: number,
  parent: Map<number, { nodeId: number; edgeId: number }>,
  edges: TrailEdge[],
  gCost: Map<number, number>
): GraphRouteResult {
  const edgeSequence: { edgeId: number; forward: boolean }[] = []
  let current = endId

  while (parent.has(current)) {
    const p = parent.get(current)!
    const edge = edges.find(e => e.id === p.edgeId)!
    edgeSequence.unshift({ edgeId: p.edgeId, forward: edge.nodeA === p.nodeId })
    current = p.nodeId
  }

  const path: [number, number][] = []
  let totalDist = 0

  for (const { edgeId, forward } of edgeSequence) {
    const edge = edges.find(e => e.id === edgeId)!
    const coords = forward ? edge.coordinates : [...edge.coordinates].reverse()
    const startIdx = path.length > 0 ? 1 : 0
    for (let i = startIdx; i < coords.length; i++) {
      path.push(coords[i])
    }
    totalDist += edge.distanceMeters
  }

  return {
    path,
    distanceMeters: totalDist,
    timeSeconds: gCost.get(endId) ?? 0,
  }
}

function computeElevationStats(
  path: [number, number][],
  sampler: ElevationSampler
): { elevGain: number; elevLoss: number } {
  let elevGain = 0
  let elevLoss = 0
  let prevElev = sampler.sample(path[0][0], path[0][1])

  for (let i = 1; i < path.length; i++) {
    const elev = sampler.sample(path[i][0], path[i][1])
    const d = elev - prevElev
    if (d > 0) elevGain += d
    else elevLoss += -d
    prevElev = elev
  }

  return { elevGain, elevLoss }
}

// ─── Graph analysis ─────────────────────────────────────────────────

function countConnectedComponents(nodes: TrailNode[], adjacency: Map<number, AdjEntry[]>): number {
  const visited = new Set<number>()
  let count = 0
  for (const node of nodes) {
    if (visited.has(node.id)) continue
    count++
    const stack = [node.id]
    while (stack.length > 0) {
      const id = stack.pop()!
      if (visited.has(id)) continue
      visited.add(id)
      for (const { targetNode } of adjacency.get(id) ?? []) {
        if (!visited.has(targetNode)) stack.push(targetNode)
      }
    }
  }
  return count
}

// ─── Geometry helpers ───────────────────────────────────────────────

function haversineMeters(a: [number, number], b: [number, number]): number {
  const toRad = Math.PI / 180
  const dlat = (b[1] - a[1]) * toRad
  const dlng = (b[0] - a[0]) * toRad
  const sinDlat = Math.sin(dlat / 2)
  const sinDlng = Math.sin(dlng / 2)
  const h = sinDlat * sinDlat + Math.cos(a[1] * toRad) * Math.cos(b[1] * toRad) * sinDlng * sinDlng
  return 6371000 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h))
}

function toblerSpeedMs(slopeRatio: number): number {
  return (6 * Math.exp(-3.5 * Math.abs(slopeRatio + 0.05))) / 3.6
}
